import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import light1 from './../Assests/light-1.jpg';
import dark1 from './../Assests/dark-1.jpg';
import { createStageBackground, deleteStageBackground, getStageBackgrounds } from "../Services/BannersApi";
import Accordion from "../Components/Accordion";
import MeetingOverlayPanel from './MeetingOverlayPanel';
import BannerPanel from "./BannerPanel";
import LogosPanel from "./LogosPanel";
import BGMPlayer from "./BGMPlayer";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FaEye, FaEyeSlash, FaTrash } from "react-icons/fa";
import { createFileChangeHandler, triggerFileInput } from "../utils/fileUploadUtils";
import ImageUploadButton from "../Components/shared/ImageUploadButton";
import ImageGrid from "../Components/shared/ImageGrid";

const MeetingBackgroundPanel = ({ panelHeight }) => {
    const { publish } = usePubSub("UPDATE_MEETING_BACKGROUND");
    const { publish: publishParticipantInfo } = usePubSub("UPDATE_PARTICIPANT_INFO");
    const mMeeting = useMeeting();

    const [backgrounds, setBackgrounds] = useState([
        { StageBackgroundID: 'default-light', StageBackgroundUrl: light1, isDefault: true },
        { StageBackgroundID: 'default-dark', StageBackgroundUrl: dark1, isDefault: true }
    ]);
    const [selectedBackground, setSelectedBackground] = useState(null);
    const [hoveredBackground, setHoveredBackground] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showParticipantInfo, setShowParticipantInfo] = useState(true);
    const fileInputRef = useRef(null);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const meetingId = queryParams.get("meetingId");

    useEffect(() => {
        fetchStageBackgrounds();
        setShowParticipantInfo(true);
    }, []);

    const fetchStageBackgrounds = async () => {
        try {
            const response = await getStageBackgrounds();
            const apiBackgrounds = response?.Data || [];
            const allBackgrounds = [
                { StageBackgroundID: 'default1', StageBackgroundUrl: light1, isDefault: true },
                { StageBackgroundID: 'default2', StageBackgroundUrl: dark1, isDefault: true },
                ...apiBackgrounds.map(bg => ({ ...bg, isDefault: false }))
            ];
            setBackgrounds(allBackgrounds);
            const selectedStageBackgroundID = localStorage.getItem('selectedStageBackgroundID');
            if (selectedStageBackgroundID) {
                const selectedBg = allBackgrounds.find(bg => bg.StageBackgroundID === selectedStageBackgroundID);
                setSelectedBackground(selectedBg?.StageBackgroundID);
            }
        } catch (error) {
            console.error('Error fetching backgrounds:', error);
        }
    };

    const handleFileChange = createFileChangeHandler(setUploading, 'StageBackgroundBase64', createStageBackground, fetchStageBackgrounds);

    const handleDeleteBackground = async (background) => {
        try {
            if (background.isDefault) {
                alert("Default backgrounds cannot be deleted.");
                return;
            }
            if (selectedBackground === background.StageBackgroundID) {
                setSelectedBackground(null);
                publish({ BackgroundId: null, BackgroundUrl: null, visibility: false }, { persist: true });
                localStorage.removeItem('selectedBackgroundId');
            }
            await deleteStageBackground(background.StageBackgroundID);
            fetchStageBackgrounds();
        } catch (error) {
            console.error("Error deleting background:", error);
        }
    };

    const handleBackgroundClick = (backgroundId) => {
        localStorage.setItem('selectedStageBackgroundID', backgroundId);
        if (selectedBackground === backgroundId) {
            setSelectedBackground(null);
            publish({ BackgroundId: null, BackgroundUrl: null, visibility: false }, { persist: true });
            localStorage.removeItem('selectedBackgroundId');
        } else {
            setSelectedBackground(backgroundId);
            const background = backgrounds.find(b => b.StageBackgroundID === backgroundId);
            publish({ BackgroundId: backgroundId, BackgroundUrl: background.StageBackgroundUrl, visibility: true }, { persist: true });
            localStorage.setItem('selectedBackgroundId', backgroundId);
        }
    };

    const handleParticipantInfoToggle = (event) => {
        const isVisible = event.target.checked;
        setShowParticipantInfo(isVisible);
        publishParticipantInfo({ showParticipantInfo: isVisible }, { persist: true });
    };

    const renderBackgroundOverlay = (background, isSelected, isHovered) => (
        <div
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: isSelected || isHovered
                    ? 'translate(-50%, -50%) scale(1)'
                    : 'translate(-50%, -50%) scale(0)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '0.5vh 1vh',
                borderRadius: '0.5vh',
                cursor: 'pointer',
                transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
                opacity: isSelected || isHovered ? 1 : 0,
                pointerEvents: isSelected || isHovered ? 'auto' : 'none',
            }}
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    handleBackgroundClick(background.StageBackgroundID);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5vh 1vh',
                    cursor: 'pointer',
                }}
            >
                <FaEye
                    style={{
                        display: isSelected ? 'none' : 'inline',
                        cursor: 'pointer',
                        fontSize: '1.6vh',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleBackgroundClick(background.StageBackgroundID);
                    }}
                />
                <FaEyeSlash
                    style={{
                        display: isSelected ? 'inline' : 'none',
                        cursor: 'pointer',
                        fontSize: '1.6vh',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleBackgroundClick(background.StageBackgroundID);
                    }}
                />
                {!background.isDefault && (
                    <FaTrash
                        style={{
                            marginLeft: '1vh',
                            cursor: 'pointer',
                            color: 'red',
                            fontSize: '1.6vh',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBackground(background);
                        }}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Box
            color="white"
            style={{ maxHeight: "80vh", overflowY: "auto", paddingTop: "0.5vh", marginTop: "1vh", paddingLeft: '1.2vw', paddingRight: '1.2vw' }}
        >
            <Box style={{ marginBottom: '2vh', padding: '1.2vh', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '0.8vh' }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showParticipantInfo}
                            onChange={handleParticipantInfoToggle}
                            sx={{
                                width: '5.2vh',
                                height: '3vh',
                                padding: '0.4vh',
                                '& .MuiSwitch-switchBase': {
                                    padding: '0.5vh',
                                    '&.Mui-checked': {
                                        color: '#429fc2',
                                        transform: 'translateX(2.2vh)',
                                    },
                                    '&.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#429fc2',
                                    },
                                },
                                '& .MuiSwitch-thumb': {
                                    width: '2vh',
                                    height: '2vh',
                                },
                                '& .MuiSwitch-track': {
                                    borderRadius: '1.4vh',
                                },
                            }}
                        />
                    }
                    label={
                        <Typography style={{ fontSize: '1.6vh', color: 'white', marginLeft: '0.8vh' }}>
                            Show Participant Info
                        </Typography>
                    }
                />
                <Typography style={{ fontSize: '1.4vh', color: '#b0b0b0', marginTop: '0.6vh', marginLeft: '4.4vh' }}>
                    Display name, designation, and mic status on recording
                </Typography>
            </Box>

            <Accordion title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8vh' }}>
                    Background
                    <Tooltip
                        title={
                            <div style={{ fontSize: '1.5vh' }}>
                                Background images serve as the backdrop for all elements added to the stage.
                                <br />
                                <strong>Recommended size:</strong> 1920 × 1080
                                <br />
                                <strong>Max file size:</strong> 4MB
                            </div>
                        }
                        arrow
                    >
                        <IconButton size="small" style={{ padding: '0.4vh' }}>
                            <HelpOutlineIcon style={{ fontSize: "2.2vh", color: "white" }} />
                        </IconButton>
                    </Tooltip>
                </div>
            }>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/png, image/jpeg"
                        onChange={handleFileChange}
                    />
                    <ImageGrid
                        items={backgrounds}
                        selectedId={selectedBackground}
                        hoveredId={hoveredBackground}
                        onHover={(id) => setHoveredBackground(id)}
                        onLeave={() => setHoveredBackground(null)}
                        onClick={(id) => handleBackgroundClick(id)}
                        getId={(bg) => bg.StageBackgroundID}
                        getUrl={(bg) => bg.StageBackgroundUrl}
                        getAlt={(bg) => `Background ${bg.StageBackgroundID}`}
                        renderOverlay={renderBackgroundOverlay}
                    />
                    <ImageUploadButton
                        uploading={uploading}
                        onUpload={() => triggerFileInput(fileInputRef)}
                        label="Add Background Image"
                    />
                </div>
            </Accordion>
            <Accordion
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8vh' }}>
                        Overlay
                        <Tooltip
                            title={
                                <div style={{ fontSize: '1.5vh' }}>
                                    Overlays are graphics on top of your content. You can use either an opaque or transparent image.
                                    <br />
                                    <strong>Recommended size:</strong> 1920 × 1080
                                    <br />
                                    <strong>Max file size:</strong> 4MB
                                </div>
                            }
                            arrow
                        >
                            <IconButton size="small" style={{ padding: '0.4vh' }}>
                                <HelpOutlineIcon style={{ fontSize: "2.2vh", color: "white" }} />
                            </IconButton>
                        </Tooltip>
                    </div>
                }
            >
                <MeetingOverlayPanel />
            </Accordion>
            <BannerPanel />
            <Accordion title="Logos">
                <LogosPanel />
            </Accordion>
        </Box>
    );
};

MeetingBackgroundPanel.propTypes = {
    panelHeight: PropTypes.any,
};

export default MeetingBackgroundPanel;
